import type { MePayload, TeamRole } from "@/lib/types";

export type PublicNavCapabilities = {
  signedIn: boolean;
  displayName: string | null;
  canOpenCoach: boolean;
  canOpenFeedback: boolean;
  canOpenAdmin: boolean;
  adminLinkLabel: "Club admin" | null;
  /** Platform super-admin console (`/super-admin`). Shown on every host for a
   * super-admin — the console itself host-gates, but the link is how they
   * reach it. */
  canOpenPlatform: boolean;
};

const COACH_ROLES = new Set<TeamRole>(["coach", "assistant_coach", "team_admin"]);
const FEEDBACK_ROLES = new Set<TeamRole>(["player", "guardian"]);

export function getPublicNavCapabilities(
  me: MePayload | null | undefined,
): PublicNavCapabilities {
  const user = me?.user;
  const memberships = me?.memberships ?? [];
  const roles = user?.roles ?? [];
  const isGlobalAdmin = !!user?.is_global_admin || roles.includes("admin");
  // A club admin administers the club from /admin but holds NO team
  // memberships, so the Admin link must key off is_club_admin too — not just
  // team roles. (Coach / My Feedback stay membership-gated: a club admin is
  // not a coach and has no coaching-content access.)
  const isClubAdmin = !!me?.is_club_admin;
  const isTeamAdmin = memberships.some((m) => m.role === "team_admin");
  const hasCoachRole = memberships.some((m) => COACH_ROLES.has(m.role));
  const hasFeedbackRole = memberships.some((m) => FEEDBACK_ROLES.has(m.role));
  const canOpenFeedback = me?.can_open_feedback ?? hasFeedbackRole;
  const isSuperAdmin = !!me?.is_super_admin;
  // The host club this request resolved to. `null` on the apex / super-admin
  // host, where there is NO club to scope Coach / My Feedback / the per-club
  // Admin console to — so those collapse and only the platform surface +
  // public Teams index remain. On a club host this is the club and the
  // club-scoped surfaces behave as before.
  const hasHostClub = !!me?.current_club;
  const canOpenAdmin = hasHostClub && (isGlobalAdmin || isClubAdmin || isTeamAdmin);

  return {
    signedIn: !!user,
    displayName:
      user?.display_name?.trim() ||
      me?.profile?.display_name?.trim() ||
      user?.username?.trim() ||
      null,
    canOpenCoach: hasHostClub && (isGlobalAdmin || hasCoachRole),
    canOpenFeedback: hasHostClub && !!user && canOpenFeedback,
    // The per-CLUB admin console is meaningless without a host club. A
    // super-admin on the apex host uses /super-admin instead (canOpenPlatform).
    canOpenAdmin,
    // This names the shared destination, not the caller's authority. Team
    // admins still receive only their assigned-team controls from the server.
    adminLinkLabel: canOpenAdmin ? "Club admin" : null,
    canOpenPlatform: isSuperAdmin,
  };
}
